import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { ArrowClockwise, DownloadSimple, ShieldCheck } from "@phosphor-icons/react";
import useBranches from "@/hooks/useBranches";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function todayISO() { return new Date().toISOString().slice(0, 10); }
function startOfMonthISO() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; }

export default function ExtendedWarrantyReportPage() {
  const [from, setFrom] = useState(startOfMonthISO());
  const [to, setTo] = useState(todayISO());
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState({ rows: [], count: 0, total_warranty_amount: 0 });
  const [loading, setLoading] = useState(false);
  const { branches } = useBranches({ activeOnly: true, includeAll: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      if (branchId) params.branch_id = branchId;
      const { data } = await api.get("/reports/extended-warranty", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [from, to, branchId]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const rows = data.rows || [];
    if (!rows.length) { toast.error("No rows to export"); return; }
    const headers = ["sale_date", "branch", "customer", "phone", "address", "pincode", "vehicle_model", "vin", "registration_no", "warranty", "amount"];
    const body = rows.map((r) => [
      (r.sale_date || "").slice(0, 10), r.branch_name, r.customer_name, r.customer_phone,
      r.customer_address, r.customer_pincode,
      r.vehicle_model, r.vin, r.registration_no, r.warranty_items, r.warranty_amount,
    ]);
    body.push(["TOTAL", "", "", "", "", "", "", "", "", "", data.total_warranty_amount]);
    const csv = [headers, ...body]
      .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `extended-warranty-${from || "all"}-${to || todayISO()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Reports"
        title="Extended Warranty Sales"
        subtitle={`${data.count ?? 0} vehicles with extended warranty · ${inr(data.total_warranty_amount)} total`}
      />

      <div className="flex items-end gap-3 flex-wrap">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ew-from"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ew-to"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm" />
        </label>
        <label className="flex flex-col gap-1 min-w-[180px]">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Branch</span>
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="ew-branch"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm">
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        <button
          onClick={load}
          disabled={loading}
          data-testid="ew-apply"
          className="px-4 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-brand disabled:opacity-50 flex items-center gap-1.5"
        >
          <ArrowClockwise size={12} /> {loading ? "Loading…" : "Apply"}
        </button>
        <button
          onClick={exportCsv}
          data-testid="ew-export"
          className="px-4 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink flex items-center gap-1.5"
        >
          <DownloadSimple size={12} /> Export CSV
        </button>
      </div>

      <div className="border border-canvas-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line">
            <tr>
              {["Sale Date", "Branch", "Customer", "Phone", "Address", "Pincode", "Vehicle", "VIN", "Reg No", "Warranty", "Amount"].map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).length === 0 ? (
              <tr>
                <td colSpan={11} className="px-3 py-10 text-center text-ink-muted text-xs">
                  <ShieldCheck size={24} className="mx-auto mb-2 opacity-40" />
                  No extended-warranty sales in this period. Warranty is an optional item on the
                  vehicle package — it appears here when ticked at sale time.
                </td>
              </tr>
            ) : (data.rows || []).map((r) => (
              <tr key={r.sale_id} className="border-b border-canvas-line/50" data-testid={`ew-row-${r.vin || r.sale_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{(r.sale_date || "").slice(0, 10)}</td>
                <td className="px-3 py-2">{r.branch_name}</td>
                <td className="px-3 py-2">{r.customer_name}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.customer_phone}</td>
                <td className="px-3 py-2 text-xs max-w-[220px] truncate" title={r.customer_address}>{r.customer_address || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.customer_pincode || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.vehicle_model}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.vin}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.registration_no || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.warranty_items}</td>
                <td className="px-3 py-2 font-mono font-semibold">{inr(r.warranty_amount)}</td>
              </tr>
            ))}
          </tbody>
          {(data.rows || []).length > 0 && (
            <tfoot className="bg-canvas-panel border-t border-canvas-line" data-testid="ew-totals">
              <tr>
                <td colSpan={10} className="px-3 py-2 font-mono text-[10px] uppercase">Total ({data.count} vehicles)</td>
                <td className="px-3 py-2 font-mono font-bold">{inr(data.total_warranty_amount)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
